'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { cn } from '../../lib/cn';
import { runRules, type Rule } from '../../lib/form';
import { withDisplayName } from '../../lib/display-name';
import { SGhostButton } from '../SGhostButton';

export interface SChipProps {
  /** 폼 연동용 name */
  name?: string;
  /** 칩 텍스트 값 */
  value?: string;
  /** 에러 상태 */
  error?: boolean;
  /** 비활성 */
  disabled?: boolean;
  /** 인라인 편집 가능 (기본값 false) */
  editable?: boolean;
  /** 닫기 버튼 표시 */
  showRemove?: boolean;
  /** 편집 중 placeholder */
  placeholder?: string;
  /** 유효성 규칙 */
  rules?: Rule[];
  /** 편집 완료 (sdUpdate) */
  onValueChange?: (value: string) => void;
  /** 삭제 (sdRemove) */
  onRemove?: () => void;
  /** 편집 진입 (sdFocus) */
  onFocus?: () => void;
  /** 편집 이탈 (sdBlur) */
  onBlur?: () => void;
  className?: string;
  style?: CSSProperties;
}

/** ref로 노출되는 UX 메서드 (sd-chip @Method sdFocus/sdGetNativeElement 대응) */
export interface SChipHandle {
  /** 편집 input에 포커스합니다. */
  focus: () => void;
  /** 네이티브 편집 엘리먼트를 반환합니다. */
  getNativeElement: () => HTMLElement | null;
}

/** SChip — sd-chip 포팅. 인라인 편집(contentEditable) + 삭제 버튼. */
const SChipImpl = forwardRef<SChipHandle, SChipProps>(function SChip(
  {
    name,
    value = '',
    error: errorProp = false,
    disabled = false,
    editable = false,
    showRemove = true,
    placeholder = '',
    rules,
    onValueChange,
    onRemove,
    onFocus,
    onBlur,
    className,
    style,
  },
  ref,
) {
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState(errorProp);
  const inputRef = useRef<HTMLSpanElement>(null);
  const draftRef = useRef(value);

  useEffect(() => setError(errorProp), [errorProp]);

  // 편집 진입 시 포커스 + 텍스트 세팅 + 커서 끝
  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current;
      el.textContent = draftRef.current;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [editing]);

  const isEditable = editable !== false && !disabled;

  const enterEdit = () => {
    if (!isEditable || editing) return;
    draftRef.current = value;
    setEditing(true);
    onFocus?.();
  };

  useImperativeHandle(ref, () => ({
    // 편집 중이면 input에 바로 포커스, 아니면 편집 모드 진입(진입 시 자동 포커스)
    focus: () => {
      if (disabled) return;
      if (editing) inputRef.current?.focus();
      else enterEdit();
    },
    getNativeElement: () => inputRef.current,
  }));

  const commit = () => {
    if (!editing) return;
    const next = (inputRef.current?.textContent ?? draftRef.current).trim();
    const changed = next !== draftRef.current;
    setEditing(false);
    if (changed) onValueChange?.(next);
    if (rules && rules.length > 0) setError(runRules(next, rules) !== '');
    onBlur?.();
  };

  const cancel = () => {
    if (!editing) return;
    setEditing(false);
    onBlur?.();
  };

  const onInputKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
  };

  // disabled 는 전용 bg/content 토큰을 가진 독립 상태라 error·editing 보다 우선한다
  // (opacity 로 겹쳐 흐리는 대신 토큰 색을 그대로 쓴다).
  const bg = disabled
    ? 'var(--cmp-chip-bg-disabled)'
    : error
      ? 'var(--cmp-chip-bg-error)'
      : editing
        ? 'var(--cmp-chip-bg-focus)'
        : 'var(--cmp-chip-bg-default)';
  const content = disabled
    ? 'var(--cmp-chip-content-disabled)'
    : error
      ? 'var(--cmp-chip-content-error)'
      : editing
        ? 'var(--cmp-chip-content-focus)'
        : 'var(--cmp-chip-content-default)';
  const borderColor = editing && !error ? 'var(--cmp-chip-border-focus)' : 'transparent';

  return (
    // 외곽 span 은 비상호작용 컨테이너다. 편집 진입 트리거는 값 텍스트의 실제 <button>
    // 이 담당하고, remove 는 형제 <button> 으로 둔다 — role="button" 안에 버튼을 넣던
    // 상호작용 요소 중첩을 제거한다. (네이티브 button 이 Enter/Space 키보드를 처리)
    <span
      aria-disabled={disabled ? 'true' : undefined}
      className={cn(
        'box-border inline-flex h-[var(--cmp-chip-height)] max-w-full items-center justify-center gap-[var(--cmp-chip-gap)] overflow-hidden whitespace-nowrap border-[length:var(--cmp-chip-border-width)] border-solid px-[var(--cmp-chip-paddingX)] text-[12px] leading-[20px] select-none',
        editing ? 'font-normal' : 'font-medium',
        disabled ? 'cursor-not-allowed' : '',
        className,
      )}
      style={{
        borderRadius: 'var(--cmp-chip-radius)',
        background: bg,
        color: content,
        borderColor,
        ...style,
      }}
    >
      {editing ? (
        <span
          ref={inputRef}
          role="textbox"
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck={false}
          data-name={name || undefined}
          data-placeholder={placeholder}
          onKeyDown={onInputKeyDown}
          onBlur={commit}
          onClick={e => e.stopPropagation()}
          onPaste={e => {
            e.preventDefault();
            const text = e.clipboardData?.getData('text/plain') ?? '';
            document.execCommand('insertText', false, text);
          }}
          className="inline-block min-w-[1ch] cursor-text bg-transparent whitespace-nowrap text-inherit outline-none [font:inherit] empty:before:pointer-events-none empty:before:opacity-60 empty:before:content-[attr(data-placeholder)]"
        />
      ) : (
        <>
          {isEditable ? (
            <button
              type="button"
              onClick={enterEdit}
              className="inline-flex min-w-0 cursor-text items-center overflow-hidden bg-transparent text-ellipsis [font:inherit] text-inherit"
            >
              {value}
            </button>
          ) : (
            <span className="inline-flex min-w-0 items-center overflow-hidden text-ellipsis">
              {value}
            </span>
          )}
          {!disabled && showRemove && (
            <SGhostButton
              icon="close"
              size="xxs"
              ariaLabel="remove"
              onClick={e => {
                e.stopPropagation();
                onRemove?.();
              }}
            />
          )}
        </>
      )}
    </span>
  );
});

export const SChip = withDisplayName(SChipImpl, 'SChip');
